import { Extension } from "@tiptap/core";
import { keymap } from "@tiptap/pm/keymap";
import { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import { Editor, NodePos } from "@tiptap/react";
import { PAGE_CONTENT_HEIGHT } from "../constants";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        pagination: {
            /**
             * Trigger pagination
             * @example editor.commands.triggerPagination()
             */
            triggerPagination: () => ReturnType;
        };
    }
}

const handleListOnBackspace = (listNode: PMNode, editor: Editor) => {
    // Check if all list items are empty
    let allEmpty =
        listNode.content.childCount === 1 &&
        listNode.content.firstChild?.textContent === "";

    if (allEmpty) {
        const type = listNode.type.name;
        const { toggleOrderedList, toggleBulletList } = editor.commands;
        type === "orderedList" ? toggleOrderedList() : toggleBulletList();
        return true;
    } else {
        return false;
    }
};

export const PaginationExtension = Extension.create({
    name: "pagination",
    addStorage() {
        return {
            isInitialLoad: true,
            updatesCounter: 0,
        };
    },
    addCommands() {
        return {
            triggerPagination:
                () =>
                ({ editor }: { editor: Editor }) => {
                    // An 'update' will be triggered by the pagination plugin.
                    // just reseting the initial load state
                    editor.storage.pagination.isInitialLoad = true;
                    editor.storage.pagination.updatesCounter = 0;
                    return true;
                },
        };
    },
    addProseMirrorPlugins() {
        const editor = this.editor;
        return [
            keymap({
                Enter: (state, dispatch, view) => {
                    // Only proceed if we have a cursor (not a selection)
                    const { from, to } = state.selection;
                    if (!dispatch || from !== to) return false;

                    const $pos = state.doc.resolve(from);
                    const parentNode = $pos.parent;

                    // Early return if not in a paragraph
                    if (parentNode.type.name !== "paragraph") return false;

                    const isEmptyParagraph = parentNode.content.size === 0;
                    const isEndOfParagraph =
                        $pos.parentOffset === parentNode.content.size;

                    // Only handle empty paragraphs or cursor at end of paragraph
                    if (!(isEmptyParagraph || isEndOfParagraph)) return false;

                    const tr = state.tr;

                    // Create and insert new paragraph
                    const newParagraph = state.schema.nodes.paragraph.create();
                    // const newParagraph = state.schema.nodes.paragraph.create(
                    //     {},
                    //     [state.schema.text(`+++ paragraph +++ ${from} +++`)]
                    // );
                    tr.insert(from, newParagraph);

                    // Set cursor position in new paragraph
                    const newPos = tr.doc.resolve(from + 1);
                    const selection = Selection.near(newPos, 1);
                    tr.setSelection(selection);

                    dispatch(tr);
                    return true;
                },

                Backspace: (state, dispatch, view) => {
                    const { from, to } = state.selection;
                    if (from < 3) return false;

                    if (!dispatch || from !== to) {
                        console.log(
                            "Early return: No dispatch or selection range"
                        );
                        return false;
                    }

                    const $pos = state.doc.resolve(from);
                    const currentNodePos = new NodePos(
                        $pos,
                        this.editor,
                        $pos.doc.isBlock
                    );
                    const pageNode = currentNodePos.closest("page");
                    if (!pageNode) return false;

                    if (pageNode.pos + 1 === from) {
                        // remove page break if exists
                        const tr = state.tr;
                        tr.delete(pageNode.pos, pageNode.pos + 1);
                        view?.dispatch(tr);
                    }

                    let depth = $pos.depth;
                    let isInList = false;
                    let listNode = null;

                    while (depth > 0) {
                        const node = $pos.node(depth);
                        const nodeType = node.type.name;
                        if (
                            nodeType === "orderedList" ||
                            nodeType === "bulletList"
                        ) {
                            isInList = true;
                            listNode = node;
                            break;
                        }
                        depth--;
                    }

                    if (isInList && listNode) {
                        return handleListOnBackspace(listNode, this.editor);
                    }
                    return false;
                },
            }),
            createPaginationPlugin(editor),
        ];
    },
});

const createPaginationPlugin = (editor: Editor) =>
    new Plugin({
        key: new PluginKey("pagination"),

        view() {
            let isPaginating = false;

            return {
                update(view: EditorView, prevState) {
                    if (isPaginating) {
                        return;
                    }
                    const selectedScale = editor.storage.scale || 1;

                    const { state } = view;

                    const { schema } = state;
                    const pageType = schema.nodes.page;

                    if (!pageType) {
                        console.log("Page node type not found in schema");
                        return;
                    }

                    const docChanged = !view.state.doc.eq(prevState.doc);

                    let hasPageNodes = false;
                    state.doc.forEach((node) => {
                        if (node.type === pageType) {
                            hasPageNodes = true;
                        }
                    });

                    const { isInitialLoad } = editor.storage.pagination;

                    if (!docChanged && hasPageNodes && !isInitialLoad) {
                        return;
                    }
                    if (
                        isInitialLoad &&
                        editor.storage.pagination.updatesCounter++ > 20 // For some reason the only many updates the pagination works perfectly
                    ) {
                        editor.storage.pagination.isInitialLoad = false;
                    }

                    isPaginating = true;

                    // Collect content nodes and their old positions
                    const contentNodes: { node: PMNode; pos: number }[] = [];
                    state.doc.forEach((node, offset) => {
                        if (node.type === pageType) {
                            node.forEach((child, childOffset) => {
                                contentNodes.push({
                                    node: child,
                                    pos: offset + childOffset + 1,
                                });
                            });
                        } else {
                            contentNodes.push({ node, pos: offset + 1 });
                        }
                    });

                    // Measure node heights
                    const MIN_PARAGRAPH_HEIGHT = 20 * selectedScale; // Adjust as needed for your styling
                    const nodeHeights = contentNodes.map(({ pos, node }) => {
                        const dom = view.nodeDOM(pos);
                        if (dom instanceof HTMLElement) {
                            let height = dom.getBoundingClientRect().height;
                            if (height === 0) {
                                if (
                                    node.type.name === "paragraph" ||
                                    node.isTextblock
                                ) {
                                    // Assign a minimum height to empty paragraphs or textblocks
                                    height = MIN_PARAGRAPH_HEIGHT;
                                }
                            }
                            return height;
                        }
                        return MIN_PARAGRAPH_HEIGHT; // Default to minimum height if DOM element is not found
                    });

                    // Record the cursor's old position
                    const { selection } = view.state;
                    const oldCursorPos = selection.from;

                    // Build the new document and keep track of new positions
                    const pages = [];
                    let currentPageContent: PMNode[] = [];
                    let currentHeight = 0;
                    const pageHeight = PAGE_CONTENT_HEIGHT * selectedScale;

                    const oldToNewPosMap: { [key: number]: number } = {};
                    let cumulativeNewDocPos = 0;
                    let skipHeightCheckForCurrentPage = false;

                    for (let i = 0; i < contentNodes.length; i++) {
                        const { node, pos: oldPos } = contentNodes[i];
                        const nodeHeight = nodeHeights[i];

                        // Check if current node is a page break
                        if (node.type.name === "pageBreak") {
                            skipHeightCheckForCurrentPage = true;

                            // Add the page break to current page
                            currentPageContent.push(node);

                            // Map the position
                            if (currentPageContent.length === 1) {
                                cumulativeNewDocPos += 1; // Start of the page node
                            }
                            oldToNewPosMap[oldPos] =
                                cumulativeNewDocPos +
                                currentPageContent.reduce(
                                    (sum, n) => sum + n.nodeSize,
                                    0
                                );

                            // Create page with current content
                            const pageNode = pageType.create(
                                {},
                                currentPageContent
                            );
                            pages.push(pageNode);
                            cumulativeNewDocPos += pageNode.nodeSize;

                            // Reset for next page
                            currentPageContent = [];
                            currentHeight = 0;
                            skipHeightCheckForCurrentPage = false; // Reset flag for next page
                            continue;
                        }

                        // Use height-based pagination unless we're in a page with a page break
                        if (
                            !skipHeightCheckForCurrentPage &&
                            currentHeight + nodeHeight > pageHeight &&
                            currentPageContent.length > 0
                        ) {
                            const pageNode = pageType.create(
                                {},
                                currentPageContent
                            );
                            pages.push(pageNode);
                            cumulativeNewDocPos += pageNode.nodeSize;
                            currentPageContent = [];
                            currentHeight = 0;
                        }

                        if (currentPageContent.length === 0) {
                            cumulativeNewDocPos += 1; // Start of the page node
                        }

                        // Record the mapping from old position to new position
                        const nodeStartPosInNewDoc =
                            cumulativeNewDocPos +
                            currentPageContent.reduce(
                                (sum, n) => sum + n.nodeSize,
                                0
                            );
                        oldToNewPosMap[oldPos] = nodeStartPosInNewDoc;

                        currentPageContent.push(node);

                        // Track height unless we're in a page with a page break
                        if (!skipHeightCheckForCurrentPage) {
                            currentHeight += Math.max(
                                nodeHeight,
                                MIN_PARAGRAPH_HEIGHT
                            );
                        }
                    }

                    // Don't forget to add the last page if it has content
                    if (currentPageContent.length > 0) {
                        const pageNode = pageType.create(
                            {},
                            currentPageContent
                        );
                        pages.push(pageNode);
                    }

                    const newDoc = schema.topNodeType.create(null, pages);

                    // Compare the content of the documents
                    if (newDoc.content.eq(state.doc.content)) {
                        isPaginating = false;
                        return;
                    }

                    const tr = state.tr.replaceWith(
                        0,
                        state.doc.content.size,
                        newDoc.content
                    );
                    tr.setMeta("pagination", true);

                    let newCursorPos = oldCursorPos;

                    // Add validation before setting selection
                    if (newCursorPos !== null) {
                        // Add validation check
                        if (newCursorPos > tr.doc.content.size) {
                            console.warn(
                                "Invalid cursor position detected, clamping to document size"
                            );
                            newCursorPos = tr.doc.content.size;
                        }

                        const $pos = tr.doc.resolve(newCursorPos);
                        let selection;

                        if ($pos.parent.isTextblock) {
                            // The position is valid for a TextSelection
                            selection = Selection.near($pos);
                        } else if (
                            $pos.nodeAfter &&
                            $pos.nodeAfter.isTextblock
                        ) {
                            // Move into the next textblock
                            selection = Selection.near(
                                tr.doc.resolve($pos.pos + 1)
                            );
                        } else if (
                            $pos.nodeBefore &&
                            $pos.nodeBefore.isTextblock
                        ) {
                            // Move into the previous textblock
                            selection = Selection.near(
                                tr.doc.resolve($pos.pos - 1),
                                -1
                            );
                        } else {
                            // Find the nearest valid cursor position
                            selection =
                                Selection.findFrom($pos, 1, true) ||
                                Selection.findFrom($pos, -1, true);
                        }

                        if (selection) {
                            tr.setSelection(selection);
                        } else {
                            // Fallback to a safe selection at the end of the document
                            tr.setSelection(
                                TextSelection.create(
                                    tr.doc,
                                    tr.doc.content.size
                                )
                            );
                        }
                    } else {
                        tr.setSelection(
                            TextSelection.create(tr.doc, tr.doc.content.size)
                        );
                    }

                    view.dispatch(tr);

                    isPaginating = false;
                },
            };
        },
    });
